/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Palette } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { BRAND_PRESETS } from '@/lib/brandPresets';

const COLOR_FIELDS = [['primaryColor', 'Primario'], ['secondaryColor', 'Secondario'], ['accentColor', 'Accento (tinta)']];
const TEXT_FIELDS = [
  ['clinicName', 'Nome clinica *', 'setup-clinic-name', ''], ['clinicShortName', 'Nome breve', 'setup-clinic-short', ''],
  ['tagline', 'Tagline', 'setup-tagline', 'sm:col-span-2'],
  ['legalOwner', 'Ragione sociale', 'setup-legal-owner', ''], ['legalVat', 'Partita IVA', 'setup-legal-vat', ''],
  ['legalAddress', 'Indirizzo', 'setup-legal-address', 'sm:col-span-2'],
  ['contactEmail', 'Email contatto', 'setup-contact-email', ''], ['contactPhone', 'Telefono', 'setup-contact-phone', ''],
  ['legalDpo', 'DPO (email)', 'setup-dpo', 'sm:col-span-2'],
];

/** Passo 3 — palette colori (preset o custom) e dati anagrafici della clinica. */
export const StepBrand = ({ form, onChange, onPreset }) => {
  const set = (k, v) => onChange({ ...form, [k]: k === 'legalVat' ? v.toUpperCase() : v });
  return (
    <div className="space-y-5 animate-fade-slide" data-testid="setup-panel-brand">
      <div>
        <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Palette className="h-5 w-5 text-primary" /> Palette e dati della clinica</h2>
        <p className="text-sm text-muted-foreground">Scegli un preset o i colori esatti del tuo brand: l'anteprima è live su tutta la pagina.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {BRAND_PRESETS.map((p) => {
          const Icon = p.icon; const active = form.primaryColor.toLowerCase() === p.primaryColor.toLowerCase();
          return (
            <button key={p.id} type="button" onClick={() => onPreset(p)} className={cn('rounded-xl border p-3 text-left transition-colors hover:border-primary/50', active ? 'border-primary bg-primary/5' : 'border-border/70')} data-testid={`setup-preset-${p.id}`}>
              <div className="flex items-center gap-2 mb-2"><Icon className="h-4 w-4" style={{ color: p.primaryColor }} /><span className="text-sm font-semibold">{p.label}</span></div>
              <div className="flex gap-1">{[p.primaryColor, p.primaryColorAlt, p.accentColor].map((c) => <span key={c} className="h-5 flex-1 rounded-md" style={{ background: c }} />)}</div>
            </button>
          );
        })}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {COLOR_FIELDS.map(([k, l]) => (
          <div key={k}>
            <Label>{l}</Label>
            <div className="flex gap-2">
              <input type="color" value={form[k]} onChange={(e) => set(k, e.target.value)} className="h-11 w-14 rounded-xl border border-border bg-card p-1 cursor-pointer" aria-label={l} data-testid={`setup-color-${k}`} />
              <Input value={form[k]} onChange={(e) => set(k, e.target.value)} className="h-11 rounded-xl font-mono uppercase" />
            </div>
          </div>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {TEXT_FIELDS.map(([k, l, tid, span]) => (
          <div key={k} className={span}><Label>{l}</Label><Input type={k === 'contactEmail' ? 'email' : 'text'} value={form[k]} onChange={(e) => set(k, e.target.value)} className={cn('h-11 rounded-xl', k === 'legalVat' && 'font-mono')} data-testid={tid} /></div>
        ))}
      </div>
    </div>
  );
};
